import { validateAgentCommand } from './agentValidation';
import { localSessionState } from '../state/localSession.svelte';
import { agentBuild } from './agentBuild';
import { updateAgentLegend } from './agentLegend';
import { captureAgentCanvas } from './agentCapture';
import { compactAgentReply } from './agentReplies';
import { setAgentCamera } from './agentTransforms';
/* eslint-disable @typescript-eslint/naming-convention -- Python MCP wire-format keys */
import * as THREE from 'three';
import { alignmentStatus, agentAlignmentBusy } from './agentAlignment';
import { applyAgentControl, fitAgentView } from './agentControls';
import { agentAttributes, rememberAgentCamera } from './agentInspection';
import { agentViewState, objectPresentation } from './agentViewState';
const rendererId = Array.from(crypto.getRandomValues(new Uint8Array(16)), n => n.toString(16).padStart(2, '0')).join('');
let previousReply;
export async function handleAgentCommand(host) {
    const response = await fetch(`agent/command?renderer_id=${rendererId}`);
    if (!response.ok) {
        return false;
    }
    const command = await response.json();
    if (!command) {
        return false;
    }
    // A wake-up can race the server clearing an acknowledged command. Replay
    // the reply instead of applying non-idempotent navigation/transforms twice.
    if (previousReply?.id === command.id) {
        await fetch('agent/result', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...previousReply, renderer_id: rendererId }),
        });
        return true;
    }
    const reply = await executeAgentCommand(host, command, true);
    previousReply = reply;
    await fetch('agent/result', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...reply, renderer_id: rendererId }),
    });
    return true;
}
/** Transport-independent executor shared by MCP and the native VS Code viewer. */
export async function executeAgentCommand(host, command, requireLocalSession = false) {
    let reply = { id: command.id };
    try {
        validateAgentCommand(command.operation, command.arguments);
        if (requireLocalSession && document.documentElement.dataset.localSession !== 'loaded') {
            throw new Error(localSessionState.error
                ? `Scene loading failed: ${localSessionState.error}`
                : 'The scene has not loaded successfully yet.');
        }
        if (command.operation === 'camera' && agentAlignmentBusy(host)) {
            throw new Error('Alignment is running; wait before changing the camera');
        }
        if (command.operation === 'camera') {
            rememberAgentCamera(host);
            const args = command.arguments;
            setAgentCamera(host, args);
            if (args.fit) {
                fitAgentView(host);
            }
        }
        const controlResult = !['camera', 'inspect', 'capture'].includes(command.operation)
            ? await applyAgentControl(host, command.operation, command.arguments)
            : undefined;
        await updateAgentLegend(host);
        host.performRender();
        const bounds = new THREE.Box3();
        for (const mesh of host.meshes) {
            if (mesh) {
                bounds.expandByObject(mesh);
            }
        }
        const attributes = await agentAttributes(host.spatialFiles);
        reply.result = {
            alignment: alignmentStatus(host),
            renderer_build: agentBuild,
            rendering: {
                webgl: !!(host.renderer.domElement.getContext('webgl2') ||
                    host.renderer.domElement.getContext('webgl')),
            },
            renderer_id: rendererId,
            rendered_revision: Number(document.documentElement.dataset.sessionRevision),
            objects: host.spatialFiles.map(file => ({
                ...objectPresentation(host, host.spatialFiles.indexOf(file)),
                object_index: host.spatialFiles.indexOf(file),
                name: file.fileName,
                source_points: file.sourcePointCount ?? null,
                filtered_points: file.sourcePointCount === undefined ? null : file.sourcePointCount - file.vertexCount,
                scalar_fields: Object.keys(file.scalarFields ?? {}),
                attributes: attributes[host.spatialFiles.indexOf(file)],
                vertices: file.vertexCount,
                faces: file.faceCount,
                has_colors: file.hasColors,
                has_normals: file.hasNormals,
                visible: host.fileVisibility[host.spatialFiles.indexOf(file)],
                point_size: host.pointSizes[host.spatialFiles.indexOf(file)],
                color_mode: host.individualColorModes[host.spatialFiles.indexOf(file)],
                points: host.pointsVisible[host.spatialFiles.indexOf(file)],
                mesh: host.solidVisible[host.spatialFiles.indexOf(file)],
            })),
            bounds: bounds.isEmpty() ? null : { min: bounds.min.toArray(), max: bounds.max.toArray() },
            ...agentViewState(host),
        };
        if (controlResult !== undefined) {
            reply.result = {
                ...reply.result,
                ...controlResult,
                camera: {
                    ...agentViewState(host).camera,
                    ...controlResult.camera,
                },
            };
        }
        if (command.arguments.detail !== 'full' && command.operation !== 'capture') {
            reply.result = compactAgentReply(command.operation, reply.result, command.arguments);
        }
        if (command.operation === 'capture' ||
            (command.operation === 'selection' && command.arguments.preview)) {
            const canvas = captureAgentCanvas(host);
            reply.result = {
                ...(command.operation === 'selection' ? reply.result : {}),
                png: canvas.toDataURL('image/png').split(',')[1],
                width: canvas.width,
                height: canvas.height,
            };
        }
    }
    catch (error) {
        reply = { id: command.id, error: error instanceof Error ? error.message : String(error) };
    }
    return reply;
}
